/**
 * Template files (.json): a whole template, exactly as stored, so it can be
 * moved between computers or handed over ready to publish (e.g. the sample in
 * docs/testing). The server checks it again on create, like every template.
 *
 * List fields point at an uploaded list by its id, and ids differ between
 * computers. On import, a list that is not on this server is matched to an
 * uploaded list that has the needed columns (a list named like "Locations"
 * first).
 */
import { Template, type FieldDef } from '@saledeed/shared';
import type { ListSummary } from '../api/types';

export const TEMPLATE_FILE_FORMAT = 'saledeed-template';
export const MAX_TEMPLATE_FILE_BYTES = 4 * 1024 * 1024;

export function templateFileText(template: Template): string {
  return `${JSON.stringify({ format: TEMPLATE_FILE_FORMAT, version: 1, template }, null, 1)}\n`;
}

/** A safe file name from the template title. */
export function templateFileName(template: Pick<Template, 'title'>): string {
  const base = template.title.replace(/[^A-Za-z0-9 _-]+/g, ' ').trim().replace(/\s+/g, '-').slice(0, 80) || 'template';
  return `${base}.template.json`;
}

export type ReadResult = { ok: true; template: Template } | { ok: false; message: string; details: string[] };

/** Accepts our file ({format, version, template}) or a bare template. */
export function readTemplateFile(text: string): ReadResult {
  let data: unknown;
  try {
    data = JSON.parse(text.replace(/^\uFEFF/, ''));
  } catch {
    return { ok: false, message: 'This is not a template file (it is not valid JSON).', details: [] };
  }
  const wrapped = data && typeof data === 'object' && 'template' in data && (data as { format?: unknown }).format === TEMPLATE_FILE_FORMAT;
  const parsed = Template.safeParse(wrapped ? (data as { template: unknown }).template : data);
  if (!parsed.success) {
    return {
      ok: false,
      message: 'This file is not a template this app can read.',
      details: parsed.error.issues.slice(0, 8).map(i => `${i.path.join('.') || '(file)'}: ${i.message}`),
    };
  }
  return { ok: true, template: parsed.data };
}

/** A list the template needs that is not on this server. */
export interface ListNeed { id: string; columns: string[]; fields: FieldDef[] }

export function missingLists(template: Template, lists: readonly ListSummary[]): ListNeed[] {
  const known = new Set(lists.map(l => l.id));
  const needs = new Map<string, ListNeed>();
  for (const f of template.fields) {
    if (f.type !== 'list' || !f.list || known.has(f.list.id)) continue;
    const need = needs.get(f.list.id) ?? { id: f.list.id, columns: [], fields: [] };
    if (!need.columns.includes(f.list.column)) need.columns.push(f.list.column);
    need.fields.push(f);
    needs.set(f.list.id, need);
  }
  return [...needs.values()];
}

/** Uploaded lists that have every column the fields need; one named like "Locations" first. */
export function listsFor(need: ListNeed, lists: readonly ListSummary[]): ListSummary[] {
  const has = (l: ListSummary) => need.columns.every(c => l.columns.includes(c));
  const score = (l: ListSummary) => (/locat/i.test(l.name) ? 0 : 1);
  return lists.filter(has).sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name));
}

/** The template with its list fields pointed at this server's lists (old id -> new id). */
export function relinkLists(template: Template, map: Readonly<Record<string, string>>): Template {
  return {
    ...template,
    fields: template.fields.map(f => (f.list && map[f.list.id] ? { ...f, list: { ...f.list, id: map[f.list.id]! } } : f)),
  };
}

/** Hands the template file to the browser as a download. */
export function downloadTemplateFile(template: Template) {
  const url = URL.createObjectURL(new Blob([templateFileText(template)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = templateFileName(template);
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
